// useAbrirConversaInbox.ts — abre (ou cria) uma conversa no INBOX INTERNO
// de WhatsApp (/whatsapp-inbox) a partir de um número de telefone cru,
// usando a conexão do PRÓPRIO usuário logado (primária → secundária →
// reserva da equipe).
//
// Substitui o antigo comportamento dos botões "WhatsApp" que abriam o
// WhatsApp externo (https://wa.me/...). Aquele caminho não passava pela
// nossa Evolution: a mensagem saía pelo WhatsApp pessoal do operador,
// não ficava registrada no CRM e não dava pra responder pelo inbox.
//
// Mesmo padrão de resolução de conexão usado em NovaConversaDialog.tsx.

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { canonicalizePhoneBR } from '@/utils/phone-canonical';
import { resolverConexaoDoUsuario } from '@/lib/conexaoDoUsuario';

export function useAbrirConversaInbox() {
  const navigate = useNavigate();
  const [abrindo, setAbrindo] = useState(false);

  const abrirConversa = useCallback(
    async (
      telefoneRaw: string | null | undefined,
      opts?: { novaAba?: boolean; leadId?: string | null; mensagemInicial?: string | null },
    ) => {
      // DDI 55 + "9" móvel obrigatório (mesma regra do envio).
      const telefone = canonicalizePhoneBR(telefoneRaw);
      if (telefone.length < 12) {
        toast.error('Número de telefone inválido para WhatsApp');
        return;
      }

      // ABA NOVA É A REGRA (Douglas 01/09/2026): abrir a conversa navegando na
      // mesma tela faz o operador perder o contexto de onde clicou (modal da
      // oportunidade, lead, carteira). Só não abre em aba se o chamador pedir
      // explicitamente { novaAba: false }.
      // Abre JÁ (dentro do gesto do clique, antes de qualquer await) p/ não ser
      // bloqueada pelo navegador; depois aponta pra conversa resolvida. Fecha se der erro.
      const emNovaAba = opts?.novaAba !== false;
      const novaAbaWin = emNovaAba ? window.open('', '_blank') : null;
      if (emNovaAba && !novaAbaWin) {
        toast.error('O navegador bloqueou a nova aba. Libere pop-ups para o CRM e tente novamente.');
        return;
      }

      setAbrindo(true);
      const toastId = toast.loading('Abrindo conversa no inbox...');
      try {
        const { data: authData } = await supabase.auth.getUser();
        const userId = authData.user?.id;
        if (!userId) {
          toast.error('Sessão expirada. Faça login novamente.', { id: toastId });
          novaAbaWin?.close();
          return;
        }

        const connectionId = await resolverConexaoDoUsuario(userId);

        if (!connectionId) {
          toast.error(
            'Você não tem uma conexão WhatsApp configurada. Configure em Meu Perfil → Minha Conexão.',
            { id: toastId },
          );
          novaAbaWin?.close();
          return;
        }

        // Cria ou encontra a conversa daquela conexão para esse número.
        // owner = usuário que clicou, pra conversa já cair como dele.
        const { data: convId, error } = await supabase.rpc(
          'whatsapp_find_or_create_conversation',
          {
            p_telefone: telefone,
            p_connection_id: connectionId,
            p_owner_user_id: userId,
          },
        );
        if (error || !convId) {
          throw error || new Error('conversation_id vazio');
        }

        if (opts?.leadId) {
          const { error: linkError } = await supabase
            .from('whatsapp_conversations')
            .update({ lead_id: opts.leadId })
            .eq('id', convId);
          if (linkError) throw linkError;
        }

        const mensagemInicial = opts?.mensagemInicial?.trim();
        if (mensagemInicial) {
          try {
            localStorage.setItem(`wa-draft-${convId}`, mensagemInicial);
          } catch {
            toast.warning('Conversa vinculada, mas o navegador não guardou a mensagem sugerida.');
          }
        }

        toast.success('Conversa aberta no inbox', { id: toastId });
        const url = `/whatsapp-inbox?conversation=${convId}`;
        if (novaAbaWin) { novaAbaWin.location.href = url; } else { navigate(url); }
      } catch (e: any) {
        console.error('[useAbrirConversaInbox] falha ao abrir conversa', e);
        toast.error('Não foi possível abrir a conversa no inbox', { id: toastId });
        novaAbaWin?.close();
      } finally {
        setAbrindo(false);
      }
    },
    [navigate],
  );

  return { abrirConversa, abrindo };
}
